<script setup lang="ts">
import { ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import AppImage from '@uiKit/elements/Image.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import PropertyDetailSection from './PropertyDetailSection.vue'
import { iconForKeyword, isIconUrl } from '../utils/propertyIconUtils'
import type { HighlightItem } from '../utils/propertyDetailUtils'

// INVESTMENT HIGHLIGHTS (Figma 32590:61851) — one card, rows split by rules,
// each with a green icon tile, a title and a one-line brief.

interface Props {
  highlights: HighlightItem[]
}

const props = defineProps<Props>()

const failed = ref<Set<string>>(new Set())
watch(
  () => props.highlights.map((highlight) => highlight.title).join('|'),
  () => {
    failed.value = new Set()
  },
)
const markFailed = (title: string) => {
  failed.value = new Set(failed.value).add(title)
}
const showImage = (highlight: HighlightItem) =>
  isIconUrl(highlight.icon) && !failed.value.has(highlight.title)
</script>

<template>
  <PropertyDetailSection v-if="highlights.length" title="Investment Highlights" gap="20px">
    <Box is="ul" direction="col" gap="10px" class="pd-highlights">
      <template v-for="(highlight, index) in highlights" :key="highlight.title">
        <Box v-if="index > 0" is="li" class="pd-highlights__rule" aria-hidden="true" />
        <Box is="li" align="center" gap="3">
          <Box align="center" justify="center" class="pd-highlights__tile">
            <AppImage
              v-if="showImage(highlight)"
              :src="highlight.icon"
              alt=""
              class="pd-highlights__img"
              @error="markFailed(highlight.title)"
            />
            <PiIcon
              v-else
              :name="iconForKeyword(`${highlight.icon} ${highlight.title}`)"
              class="pd-highlights__icon"
            />
          </Box>
          <Box direction="col" gap="6px" class="pd-highlights__text">
            <Text
              type="heading"
              weight="semibold"
              color="var(--p-sliceal-gray-900)"
              is="span"
              style="line-height: 24px"
              >{{ highlight.title }}</Text
            >
            <Text
              v-if="highlight.description"
              type="body"
              color="muted"
              is="span"
              style="line-height: 20px"
            >
              {{ highlight.description }}
            </Text>
          </Box>
        </Box>
      </template>
    </Box>
  </PropertyDetailSection>
</template>

<style scoped>
.pd-highlights {
  margin: 0;
  padding: 16px;
  list-style: none;
  border: 1px solid var(--p-sliceal-gray-200);
  border-radius: 10px;
  background: var(--p-surface-0);
}

.pd-highlights__rule {
  height: 1px;
  background: var(--p-sliceal-gray-200);
}

.pd-highlights__tile {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border: 1px solid var(--p-sliceal-gray-100);
  border-radius: 12px;
  background: var(--p-surface-0);
}

.pd-highlights__img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.pd-highlights__icon {
  font-size: 18px;
  color: var(--p-emerald-600);
}

.pd-highlights__text {
  min-width: 0;
}
</style>
